import React, { useCallback, useEffect, useState } from "react";

import { faPlay } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useFeatureIsOn } from "@growthbook/growthbook-react";
import cx from "classnames";
import { noop } from "lodash";

import { Button } from "~/ui";

import styles from "./PlayButtonSpeedControls.module.css";

export function useSpeedToolbarEnabled() {
	return useFeatureIsOn("speed-toolbar");
}

function BigPlayButtonIcon({ onClick }: { onClick: () => void }) {
	return <span className={styles.bigPlayButtonIcon} onClick={onClick} />;
}

export interface PlayButtonSpeedControlsProps {
	playVideo: () => void;
	setPlaybackRate: (rate: number) => void;
	duration: number;
	playbackRate: number;
	isExpanded: boolean;
	compact: boolean;
}

const SPEED_OPTIONS = [1, 1.25, 1.5, 1.75, 2];

const formatDuration = (minutes: number): string => {
	const totalMinutes = minutes;
	const hours = Math.floor(totalMinutes / 60);
	const mins = Math.floor(totalMinutes % 60);
	const secs = Math.round((totalMinutes - Math.floor(totalMinutes)) * 60);

	if (hours >= 1) {
		if (mins === 0) {
			return `${hours} hr`;
		}
		return `${hours} hr ${mins} min`;
	}

	if (mins >= 1) {
		if (secs === 0) {
			return `${mins} min`;
		}
		return `${mins} min ${secs} sec`;
	}

	return `${secs} sec`;
};

export const PlayButtonSpeedControls: React.FC<PlayButtonSpeedControlsProps> = ({
	setPlaybackRate,
	playbackRate,
	playVideo,
	duration,
	isExpanded,
	compact,
}) => {
	const [isAnimating, setIsAnimating] = useState(false);
	// Preview playback rate is used to show the speed change (on hover) before the playback rate is updated (on click)
	const [previewPlaybackRate, setPreviewPlaybackRate] = useState<number | undefined>(undefined);

	const displayedPlaybackRate = previewPlaybackRate || playbackRate;
	const originalDurationFormatted = formatDuration(duration / 60);
	const newDurationFormatted = formatDuration(duration / 60 / displayedPlaybackRate);

	const handleSpeedChange = useCallback(
		(speed: number) => {
			setIsAnimating(false);
			setPlaybackRate(speed);
		},
		[setPlaybackRate],
	);

	const handlePreviewSpeedChange = useCallback(
		(speed: number | undefined) => {
			if (speed !== playbackRate && speed !== previewPlaybackRate) {
				setPreviewPlaybackRate(speed);
				if (speed !== undefined || playbackRate !== previewPlaybackRate) {
					setIsAnimating(true);
				}
			}
		},
		[playbackRate, previewPlaybackRate],
	);

	useEffect(() => {
		if (isAnimating && isExpanded) {
			const timer = setTimeout(() => setIsAnimating(false), 300);
			return () => clearTimeout(timer);
		}
		return noop;
	}, [isAnimating, isExpanded]);

	return (
		<div className={styles.speedControlsContainer}>
			<div className={styles.playButton}>
				{!compact && <BigPlayButtonIcon onClick={playVideo} />}
				{compact && (
					<Button onClick={playVideo} className={styles.playButtonCompact}>
						<FontAwesomeIcon icon={faPlay} />
						Play
					</Button>
				)}
			</div>
			{duration > 0 && (
				<div className={styles.speedInfoContainer}>
					<SpeedInfoRow
						displayedPlaybackRate={displayedPlaybackRate}
						isExpanded={isExpanded}
						originalDurationFormatted={originalDurationFormatted}
						newDurationFormatted={newDurationFormatted}
						isAnimating={isAnimating}
						compact={compact}
					/>
					<SpeedButtonsContainer
						compact={compact}
						isExpanded={isExpanded}
						playbackRate={playbackRate}
						handleSpeedChange={handleSpeedChange}
						handlePreviewSpeedChange={handlePreviewSpeedChange}
					/>
				</div>
			)}
		</div>
	);
};

interface SpeedInfoRowProps {
	displayedPlaybackRate: number;
	isExpanded: boolean;
	originalDurationFormatted: string;
	newDurationFormatted: string;
	isAnimating: boolean;
	compact: boolean;
}

const SpeedInfoRow: React.FC<SpeedInfoRowProps> = ({
	displayedPlaybackRate,
	isExpanded,
	originalDurationFormatted,
	newDurationFormatted,
	isAnimating,
	compact,
}) => {
	if (displayedPlaybackRate === 1) {
		return (
			<div className={cx(styles.speedInfoRow, { [styles.speedInfoRowCompact]: compact })}>
				<div className={cx(styles.speedInfoRowContent, styles.speedInfoRowContentSameSpeed)}>
					<span
						className={cx(styles.newDuration, {
							[styles.animateNewDuration]: isAnimating,
							[styles.newDurationCompact]: compact,
						})}
					>
						{newDurationFormatted}
					</span>
				</div>
			</div>
		);
	}

	return (
		<div className={cx(styles.speedInfoRow, { [styles.speedInfoRowCompact]: compact })}>
			<div
				className={cx(styles.speedInfoRowContent, {
					[styles.speedInfoRowContentOpen]: isExpanded,
				})}
			>
				<div
					className={cx(styles.durationComparisonContainer, {
						[styles.durationComparisonContainerCompact]: compact,
					})}
				>
					{displayedPlaybackRate > 1 && <span className={styles.originalDuration}>{originalDurationFormatted}</span>}
					<span
						className={cx(styles.newDuration, {
							[styles.animateNewDuration]: isAnimating,
							[styles.newDurationSlow]: displayedPlaybackRate <= 1,
							[styles.newDurationCompact]: compact,
						})}
					>
						{`⚡ ${newDurationFormatted}`}
					</span>
				</div>
			</div>
		</div>
	);
};

interface SpeedButtonsContainerProps {
	isExpanded: boolean;
	playbackRate: number;
	handleSpeedChange: (speed: number) => void;
	handlePreviewSpeedChange: (speed: number | undefined) => void;
	compact: boolean;
}

const SpeedButtonsContainer: React.FC<SpeedButtonsContainerProps> = ({
	isExpanded,
	playbackRate,
	handleSpeedChange,
	handlePreviewSpeedChange,
	compact,
}) => (
	<div
		className={cx(styles.speedButtonsContainer, {
			[styles.speedButtonsContainerOpen]: isExpanded,
			[styles.speedButtonsContainerCompact]: compact,
		})}
	>
		{SPEED_OPTIONS.map(speed => (
			<button
				key={speed}
				className={cx(styles.speedButton, {
					[styles.selected]: speed === playbackRate,
					[styles.speedButtonCompact]: compact,
				})}
				onClick={() => handleSpeedChange(speed)}
				onMouseEnter={() => handlePreviewSpeedChange(speed)}
				onMouseLeave={() => handlePreviewSpeedChange(undefined)}
			>
				{speed}x
			</button>
		))}
	</div>
);
